import React, {PureComponent} from 'react';
import {UrlBuilder} from "../../../core/url/UrlBuilder";
import {UserLocalStorage} from "../../../core/storage/UserLocalStorage";
import moment from "moment";
import Dialog from "@mui/material/Dialog";
import Fab from "@mui/material/Fab";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import Zoom from "@mui/material/Zoom";
import Skeleton from '@mui/material/Skeleton';
import {UserContext} from "../../../core/UserContext";
import {Link as RouterLink} from "react-router-dom";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import {ColorPicker} from "../../../core/colors/ColorPicker";
import {DatePrettifier} from "../../../core/prettifier/DatePrettifier";
import {withRouter} from "../../../hooks/withRouter";
import Slide from "@mui/material/Slide";
import FitnessCenterIcon from "@mui/icons-material/FitnessCenter";
import AddIcon from "@mui/icons-material/Add";
import Button from "@mui/material/Button";

const Transition = React.forwardRef(function Transition(props, ref) {
	return <Slide direction="up" ref={ref} {...props} />;
});

class HomeFabButton extends PureComponent {
	constructor(props) {
		super(props);

		this.state = {
			routines: UserLocalStorage.get(UrlBuilder.routine.routinesApi(null, true)) ?? null,
			isLoading: true,
		};
	}

	hasNeverBeenFetched = () => {
		return this.state.routines === null;
	}

	fetch = async () => {
		this.setState({ isLoading: true });

		const response = await fetch(UrlBuilder.routine.routinesApi(null, true));
		const routines = await response.json();
		this.setState({
			routines: routines,
			isLoading: false
		});

		UserLocalStorage.set(UrlBuilder.routine.routinesApi(null, true), routines ?? []);
	}

	startWorkout = async (event, routine) => {
		event.preventDefault();

		this.setState({ isLoading: true });

		let workout = {
			dateTimeStarted: moment().format('L, h:mm:ss A')
		};

		let res = await fetch(UrlBuilder.workout.startWorkoutApi(), {
			method: 'POST',
			headers: {
				'Accept': 'application/json',
				'Content-Type': 'application/json'
			},
			body: JSON.stringify(workout)
		}).then(res => res.json());

		this.context.setUser(res.data);

		this.props.navigate(UrlBuilder.routine.routineDetailPage(routine.id));
	}

	renderLoadingDialog = () => (
		<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
			{[1, 2, 3].map(i => (
				<Skeleton key={i} variant="rounded" height={64} sx={{ borderRadius: 2 }} />
			))}
		</Box>
	)

	renderNoRoutinesDialog = () => (
		<Box sx={{ textAlign: 'center', py: 4, px: 2 }}>
			<FitnessCenterIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1.5 }} />
			<Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
				No routines yet
			</Typography>
			<Typography variant="body2" sx={{ color: 'text.secondary', mb: 2.5 }}>
				Create a routine to start tracking your workouts.
			</Typography>
			<Button
				component={RouterLink}
				to={UrlBuilder.routine.routineOverviewPage(true)}
				variant="contained"
				color="secondary"
				startIcon={<AddIcon />}
				sx={{ textTransform: 'none', fontWeight: 600 }}
			>
				Create a Routine
			</Button>
		</Box>
	)

	renderRoutines = () => (
		<Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
			{this.state.routines.map((routine) => {
				const color = routine.color ?? ColorPicker.pick(routine.name);
				const lastTrained = routine.dateLastLogged
					? DatePrettifier.prettifyLastTrained(new Date(routine.dateLastLogged))
					: null;

				return (
					<Box
						key={routine.id}
						onClick={(e) => this.startWorkout(e, routine)}
						sx={{
							display: 'flex', alignItems: 'center', gap: 1.5,
							p: 1.5, borderRadius: 2, cursor: 'pointer',
							transition: 'background 0.15s',
							'&:hover': {
								bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
							},
							'&:active': {
								bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
							},
						}}
					>
						<Box sx={{
							width: 42, height: 42, borderRadius: '50%',
							bgcolor: color,
							display: 'flex', alignItems: 'center', justifyContent: 'center',
							flexShrink: 0,
						}}>
							<Typography sx={{ color: 'white', fontWeight: 700, fontSize: 17 }}>
								{routine.name.charAt(0).toUpperCase()}
							</Typography>
						</Box>
						<Box sx={{ flex: 1, minWidth: 0 }}>
							<Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
								{routine.name}
							</Typography>
							{lastTrained && (
								<Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.3 }}>
									{lastTrained}
								</Typography>
							)}
						</Box>
						<PlayArrowIcon sx={{ fontSize: 20, color: 'text.disabled', flexShrink: 0 }} />
					</Box>
				);
			})}
		</Box>
	)

	renderPopUp = () => {
		if (!this.state.isRoutinesPopUpOpen) {
			return null;
		}

		let content;
		if (this.hasNeverBeenFetched()) {
			content = this.renderLoadingDialog();
		} else if (this.state.routines.length === 0) {
			content = this.renderNoRoutinesDialog();
		} else {
			content = this.renderRoutines();
		}

		return (
			<Dialog
				onClose={this.closeRoutinesPopUp}
				open={true}
				TransitionComponent={Transition}
				fullWidth
				maxWidth="xs"
				PaperProps={{ sx: { borderRadius: 3 } }}
			>
				<Box sx={{ px: 2.5, pt: 2.5, pb: 1 }}>
					<Typography variant="h6" sx={{ fontWeight: 700, fontSize: 19 }}>
						Start a Workout
					</Typography>
					{this.state.routines && this.state.routines.length > 0 && (
						<Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.25 }}>
							Pick a routine to begin.
						</Typography>
					)}
				</Box>

				<Box sx={{ px: 2, pb: 2.5, pt: 0.5 }}>
					{content}
				</Box>
			</Dialog>
		);
	}

	openRoutinesPopUp = () => {
		this.setState({ isRoutinesPopUpOpen: true });
		this.fetch();
	}

	closeRoutinesPopUp = () => {
		this.setState({ isRoutinesPopUpOpen: false });
	}

	render() {
		if (this.context.user && this.context.user.activeWorkout) {
			return null;
		}

		return <>
			<Zoom in={true} unmountOnExit>
				<Fab
					color="primary"
					onClick={this.openRoutinesPopUp}
					sx={{
						margin: 0,
						top: 'auto',
						right: 35,
						bottom: 80,
						left: 'auto',
						position: 'fixed',
					}}
				>
					<PlayArrowIcon />
				</Fab>
			</Zoom>
			{this.renderPopUp()}
		</>;
	}
}

HomeFabButton.contextType = UserContext;

export default withRouter(HomeFabButton);
